import { MAX_ATTACHMENT_BYTES } from '../../../../shared/uploadLimits';

export type UploadedContext = {key:string;r2_key:string;content_type:string;name:string};
type Transport = (action:string,params:Record<string,string>,body?:unknown,onProgress?:(loaded:number,total:number)=>void)=>Promise<Response>;

class UploadError extends Error {
  constructor(message:string,readonly status:number) { super(message); }
}
async function readResponse<T>(response:Response):Promise<T> {
  const body = await response.json().catch(()=>null);
  if (!response.ok) throw new UploadError(body?.error?.message || `Upload failed (${response.status}).`,response.status);
  return body as T;
}
async function retry<T>(fn:()=>Promise<T>):Promise<T> {
  for (let attempt=0;;attempt++) {
    try { return await fn(); }
    catch (error) {
      if (attempt >= 2 || (error instanceof UploadError && error.status < 500) || (error instanceof Error && error.name === 'AuthExpiredError')) throw error;
      await new Promise(resolve=>setTimeout(resolve,500*(attempt+1)));
    }
  }
}

export async function uploadContextMultipart(file:File,section:string,skillGroup:string|undefined,send:Transport,onProgress?:(loaded:number,total:number)=>void):Promise<UploadedContext> {
  if (file.size > MAX_ATTACHMENT_BYTES) throw new Error('File is too large (max 200 MB).');
  const session = await readResponse<{id:string;part_size:number}>(await send('create',{}, {name:file.name,size:file.size,contentType:file.type,section,skillGroup}));
  let completing = false;
  try {
    for (let offset=0,part=1;offset<file.size;offset+=session.part_size,part++) {
      const blob = file.slice(offset,Math.min(file.size,offset+session.part_size),'application/octet-stream');
      await retry(async()=>readResponse(await send('part',{id:session.id,part:String(part)},blob,(loaded,total)=>{
        onProgress?.(Math.min(file.size*0.99,offset+blob.size*(total ? loaded/total : 0)),file.size);
      })));
    }
    completing = true;
    const result = await retry(async()=>readResponse<UploadedContext>(await send('complete',{id:session.id})));
    onProgress?.(file.size,file.size);
    return result;
  } catch (error) {
    // Don't destroy an upload after completion may have succeeded server-side.
    if (!completing) await send('abort',{id:session.id}).catch(()=>{});
    throw error;
  }
}
